import { connectSession } from "./session";
import {
  CONTACT_EVENT_STATE_MAP,
  CONTACT_STATE_MAP,
  CHANNEL_TYPES,
  LOG_PREFIX,
} from "../../constants/constants";
import { formatCaseAttributes } from "../../utils";
import { changeAgentStateToAvailable } from "./agent";
import { clearCurrentCallbackContact } from "../api/callback";
import { Callback } from "../../types/callback.type";
import Emitter from "../../emitter";
import { API_CONTACT_EVENT_LOGGING, STATE_CALLBACK, STATE_MISSED_CALLBACK } from "../constants";
import { API_MILO_CALLBACK_CHANNEL_TRANSACTION } from "../../constants/events";
import storeRTK from "../../store/storeRTK";
import Player from "../../utils/audio/player";

const lp = LOG_PREFIX + "[handlers.callback.ts]: ";

export const callbackPlayer = new Player({ type: "CALLBACK", loop: true });

const defaultValue = {
  self: {
    callback: {},
  },
  customer: {},
};
export function handleIncomingCallback(contact: any) {
  console.log(lp + " INCOMING callback contact: ", contact);
  updateCallbackAndSend(CONTACT_EVENT_STATE_MAP.INCOMING, contact);
  return {
    contactId: contact.contactId || contact.getContactId(),
    eventType: CONTACT_EVENT_STATE_MAP.RINGING,
    channel: CHANNEL_TYPES.CALLBACK,
  };
}
export function handleConnectingCallback(contact: any) {
  console.log(lp + " CONNECTING callback contact: ", contact);
  updateCallbackAndSend(CONTACT_EVENT_STATE_MAP.CONNECTING, contact);
}

export function handleAcceptedCallback(contact: any) {
  console.log(
    lp +
      " ACCEPTED Contact accepted by agent. Contact state is " +
      contact.getStatus().type,
    contact
  );
  updateCallbackAndSend(CONTACT_EVENT_STATE_MAP.ACCEPTED, contact);
}
export function handleConnectedCallback(contact: any) {
  console.log(
    lp +
      " CONNECTED callback contact connected to agent. Contact state is " +
      contact.getStatus().type
  );
  updateCallbackAndSend(CONTACT_EVENT_STATE_MAP.CONNECTED, contact);
  // Send message to Notification Manager to call contactEvent api
  Emitter.emit(API_CONTACT_EVENT_LOGGING, {
    chan: API_CONTACT_EVENT_LOGGING,
    body: {
      contactId: contact?.contactId || contact?.getContactId(),
      eventType: CONTACT_EVENT_STATE_MAP.ESTABLISHED,
      channel: CHANNEL_TYPES.VOICE,
    },
  });

  Emitter.emit(API_MILO_CALLBACK_CHANNEL_TRANSACTION, {
    body: {
      contactId: contact.contactId || contact.getContactId(),
    },
  });
}

export function handleRefreshCallback(contact: any) {
  console.log(lp + " REFRESH callback contact: ", contact);
  try {
    updateCallbackAndSend(CONTACT_EVENT_STATE_MAP.REFRESH, contact);
  } catch (e) {
    handleEndedCallback(contact);
  }
}
export function handleMissedCallback(contact: any) {
  console.log(lp + " MISSED callback contact: ", contact);
  
  // Get customer information for the missed callback notification
  const customerEndpoint = contact.getActiveInitialConnection()?.getEndpoint();
  const customerAttributes = formatCaseAttributes(contact.getAttributes());
  const queueInfo = contact.getQueue();
  const agentConnection = contact.getAgentConnection();
  
  // Send message to Notification Manager to call contactEvent api
  Emitter.emit(API_CONTACT_EVENT_LOGGING, {
    chan: API_CONTACT_EVENT_LOGGING,
    body: {
      contactId: contact?.contactId || contact?.getContactId(),
      eventType: CONTACT_EVENT_STATE_MAP.MISSED,
      channel: CHANNEL_TYPES.VOICE,
    },
  });

  // Emit enhanced missed callback event for notifications
  Emitter.emit(STATE_MISSED_CALLBACK, {
    chan: STATE_MISSED_CALLBACK,
    body: {
      contactId: contact.contactId || contact.getContactId(),
      eventType: CONTACT_EVENT_STATE_MAP.MISSED,
      channel: CHANNEL_TYPES.VOICE,
      // Customer information
      customerName: customerAttributes?.FIRSTNAME + ' ' + customerAttributes?.LASTNAME ||
                  customerEndpoint?.name || 
                  'Unknown Customer',
      phoneNumber: customerAttributes?.PHONE_NUMBER || 
                  'Unknown Number',
      // Queue information
      queueName: queueInfo?.name || 'Unknown Queue',
      queueId: queueInfo?.queueARN || null,
      // Contact details
      direction: contact.isInbound() ? 'INBOUND' : 'OUTBOUND',
      contactStartTime: agentConnection.getState().timestamp || new Date().toISOString(),
      missedAt: new Date().toISOString(),
      // Additional attributes that might be useful
      customerAttributes: customerAttributes,
      // Agent context
      agentId: agentConnection?.connectionId || null,
      // Contact duration (if available)
      contactDuration: (new Date().getTime() - new Date(agentConnection.getState().timestamp).getTime()) || 0,
      // Callback specific information
      scheduledTime: customerAttributes?.Scheduled_Time || null,
    },
  });

  // changeAgentStateToAvailable();
  clearCurrentCallbackContact(contact.contactId || contact.getContactId());
  updateCallbackAndSend(CONTACT_EVENT_STATE_MAP.MISSED, contact);
}

export function handleDestroyCallback(contact: any) {
  console.log(lp + " DESTROY callback contact: ", contact);
  updateCallbackAndSend(CONTACT_EVENT_STATE_MAP.DESTROYED, contact);
  // Send message to Notification Manager to call contactEvent api
  Emitter.emit(API_CONTACT_EVENT_LOGGING, {
    chan: API_CONTACT_EVENT_LOGGING,
    body: {
      contactId: contact?.contactId || contact?.getContactId(),
      eventType: CONTACT_EVENT_STATE_MAP.RELEASED,
      channel: CHANNEL_TYPES.VOICE,
    },
  });
}
export function handleEndedCallback(contact: any) {
  console.log(
    lp +
      " ENDED callback contact has ended. Contact state is " +
      contact.getStatus().type
  );
  updateCallbackAndSend(CONTACT_EVENT_STATE_MAP.ENDED, contact);
  // Send message to Notification Manager to call contactEvent api
  Emitter.emit(API_CONTACT_EVENT_LOGGING, {
    chan: API_CONTACT_EVENT_LOGGING,
    body: {
      contactId: contact?.contactId || contact?.getContactId(),
      eventType: CONTACT_EVENT_STATE_MAP.RELEASED,
      channel: CHANNEL_TYPES.VOICE,
    },
  });
  if (contact.getStatus().type === "error") {
    console.log(lp + "Error in callback contact", contact.getStatus().error);
  }
}

export function updateCallbackAndSend(type: any, contact: any) {
  try {
    // Create a deep copy instead of a shallow copy
    const vm = JSON.parse(
      JSON.stringify(connectSession.getCallback() || defaultValue)
    );

    const updatedCb = updateCallback(type, contact);

    // Handle null updatedVm case and perform deep comparison
    if (!updatedCb) return;

    // Compare objects differently to detect nested changes
    const hasChanged = JSON.stringify(vm) !== JSON.stringify(updatedCb);
    if (!hasChanged) return;

    connectSession.setCallback(updatedCb);
    Emitter.emit(STATE_CALLBACK, { body: updatedCb });
  } catch (error) {
    console.error(lp + "Error in updateCallbackAndSend", error);
  }
}

function updateCallback(
  contactEventType: string,
  contact: any
): Callback | null {
  try {
    const cb = connectSession.getCallback() || { ...defaultValue };
    // needed as INITIAL/CONNECTING event overwrites the connectedTime on refresh
    const callbackState = storeRTK.getState()?.callback;

    switch (contactEventType) {
      case CONTACT_EVENT_STATE_MAP.INITIAL: {
        const callbackAttributes = formatCaseAttributes(
          contact.getAttributes()
        );
        return {
          ...cb,
          id: contact.contactId || contact.getContactId(),
          selfState: CONTACT_STATE_MAP.INCOMING,
          conversationMedia: "CALLBACK",
          self: {
            ...cb.self,
            id: contact.getAgentConnection().getConnectionId(),
            userId: contact.getAgentConnection().getConnectionId(),
            queueId: contact.getAgentConnection().getConnectionId(),
            type: "AGENT",
            attributes: {},
            state: CONTACT_STATE_MAP.INCOMING,
            callback: {
              ...cb.self.callback,
              id: contact.contactId || contact.getContactId(),
              callbackId: contact.contactId || contact.getContactId(),
              callbackNumber: callbackAttributes.PHONE_NUMBER,
              state: CONTACT_STATE_MAP.INCOMING,
              connectedTime:
                callbackState?.self?.callback?.connectedTime || null,
              endTime: callbackState?.self?.callback?.connectedTime || null,
            },
          },
          customer: {
            ...cb.customer,
            state: CONTACT_STATE_MAP.CONNECTED,
            attributes: callbackAttributes,
            address: callbackAttributes.PHONE_NUMBER,
            type: "CUSTOMER",
          },
        };
      }

      case CONTACT_EVENT_STATE_MAP.CONNECTED: {
        const resolvedConnectedTime =
          callbackState?.self?.callback?.connectedTime || new Date().getTime();

        return {
          ...cb,
          selfState: CONTACT_STATE_MAP.CONNECTED,
          self: {
            ...cb.self,
            state: CONTACT_STATE_MAP.CONNECTED,
            connectedTime: resolvedConnectedTime,
            callback: {
              ...cb.self.callback,
              state: CONTACT_STATE_MAP.CONNECTED,
              connectedTime: resolvedConnectedTime,
            },
          },
        };
      }

      case CONTACT_EVENT_STATE_MAP.CONNECTING:
      case CONTACT_EVENT_STATE_MAP.REFRESH:
        return {
          ...cb,
          customer: {
            ...cb.customer,
            attributes: formatCaseAttributes(contact.getAttributes()),
          },
        };
      case CONTACT_EVENT_STATE_MAP.MISSED:
        return {
          ...cb,
          self: {
            ...cb.self,
            missed: true,
          },
        };

      case CONTACT_EVENT_STATE_MAP.ENDED:
      case CONTACT_EVENT_STATE_MAP.DESTROYED: {
        return {
          ...cb,
          self: {
            ...cb.self,
            state: CONTACT_STATE_MAP.ENDED,
            endTime: cb.self.callback.endTime || new Date().getTime(),
            callback: {
              ...cb.self.callback,
              state: CONTACT_STATE_MAP.ENDED,
              endTime: cb.self.callback.endTime || new Date().getTime(),
            },
            missed: [CONTACT_EVENT_STATE_MAP.REJECTED, CONTACT_EVENT_STATE_MAP.MISSED].includes(contact.getState()?.type?.toUpperCase())
          },
        };
      }

      default:
        return cb;
    }
  } catch (err) {
    console.log(
      lp +
        " Error in updating callback contactId: " +
        contact.getContactId() +
        err
    );
    return null;
  }
}
